import * as React from "react"
import { Button, Heading, Spacer, ThemeCss } from "gatsby-interface"
import { PlanTierInfo } from "@modules/billing/shared/components/PlanTierInfo"
import { emphesizedWordCss } from "@modules/billing/shared/components/PlanTierSelector"
import {
  supportedTiers,
  PlanInfo,
  SupportedPricingTier,
} from "@modules/billing/shared/constants/tiers"
import capitalizeStr from "@modules/toolkit/helpers/capitalizeStr"

const rootCss: ThemeCss = theme => ({
  display: `grid`,
  marginTop: theme.space[12],
  boxShadow: theme.shadows.raised,
  border: `2px solid ${theme.colors.purple[10]}`,
  position: `relative`,
  bottom: 0,
  background: theme.colors.white,
  zIndex: theme.zIndices.toasts,
  padding: `${theme.space[7]} ${theme.space[7]}`,
  alignItems: `end`,
  columnGap: theme.space[10],
  rowGap: theme.space[3],

  [theme.mediaQueries.desktop]: {
    gridTemplateColumns: `1fr max-content max-content`,
    padding: `${theme.space[8]} ${theme.space[10]}`,
  },
})

const columnCss: ThemeCss = theme => ({
  display: `grid`,
  alignItems: `end`,
  rowGap: theme.space[2],
  justifyContent: `start`,

  small: {
    color: theme.colors.grey[50],
  },

  "&:not(:first-of-type)": {
    marginTop: theme.space[5],
  },

  [theme.mediaQueries.desktop]: {
    "&:not(:first-of-type)": {
      marginTop: 0,
    },
  },
})

const headingCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[5],
  marginBottom: theme.space[3],
})

const actionsCss: ThemeCss = _ => ({
  display: `flex`,
})

export type PlanSelectionSummaryProps = {
  planInfo: PlanInfo
  onNext: () => void
  helpWidget?: React.ReactNode
  showPrices?: boolean
}

export function PlanSelectionSummary({
  planInfo,
  helpWidget,
  onNext,
  showPrices = true,
}: PlanSelectionSummaryProps) {
  const { buildsTier, hostingTier, billingInterval } = planInfo

  let total
  let saving

  const buildsPrices = buildsTier
    ? supportedTiers[buildsTier as SupportedPricingTier].buildsMonthlyAmount
    : null
  const hostingPrices = hostingTier
    ? supportedTiers[hostingTier as SupportedPricingTier].hostingMonthlyAmount
    : null

  if (
    buildsPrices &&
    buildsPrices.MONTHLY !== null &&
    buildsPrices.ANNUAL !== null &&
    hostingPrices &&
    hostingPrices.MONTHLY !== null &&
    hostingPrices.ANNUAL !== null
  ) {
    const monthlyTotal = (buildsPrices.MONTHLY + hostingPrices.MONTHLY) / 100
    const annualTotal =
      ((buildsPrices.ANNUAL + hostingPrices.ANNUAL) * 12) / 100

    total = billingInterval === `ANNUAL` ? annualTotal : monthlyTotal
    saving = monthlyTotal * 12 - annualTotal
  }

  return (
    <div
      css={theme => [
        rootCss(theme),
        (buildsTier || hostingTier) && {
          [theme.mediaQueries.desktop]: {
            position: `sticky`,
          },
        },
      ]}
    >
      <div css={columnCss}>
        <Heading as="h4" fontVariant="UI" css={headingCss}>
          <span css={emphesizedWordCss}>Your</span> plan:
        </Heading>
        <PlanTierInfo
          tierType="builds"
          tier={planInfo.buildsTier as SupportedPricingTier}
          showTip={true}
          showDetails={false}
          showPrice={showPrices}
          billingInterval={billingInterval}
        />
        <PlanTierInfo
          tierType="hosting"
          tier={planInfo.hostingTier as SupportedPricingTier}
          showTip={true}
          showDetails={false}
          showPrice={showPrices}
          billingInterval={billingInterval}
        />
      </div>

      <div css={columnCss}>
        {showPrices && buildsTier && hostingTier && (
          <React.Fragment>
            <strong>Total:</strong>
            <span>
              {capitalizeStr({
                str: billingInterval.toLowerCase(),
              })}
              : <strong>${total}</strong>
            </span>
            {billingInterval === `ANNUAL` && !!saving && (
              <small>
                You’re saving <b>${saving}</b> by choosing annual billing
              </small>
            )}
          </React.Fragment>
        )}
      </div>
      <div css={columnCss}>
        <div css={actionsCss}>
          <Button
            onClick={onNext}
            type="submit"
            disabled={!buildsTier || !hostingTier}
          >
            Select this plan
          </Button>
          {helpWidget && (
            <React.Fragment>
              <Spacer direction="horizontal" size={5} />
              {helpWidget}
            </React.Fragment>
          )}
        </div>
        {!!total && total > 0 && (
          <small>
            <b>14 day trial</b>, no credit card required
          </small>
        )}
      </div>
    </div>
  )
}
